/**
 * alyx — Pedido de tests con crédito de membresía
 * =================================================
 * Vive en: assets/alyx-order-tests.js (theme alyxbio.com)
 * Requiere en theme.liquid (solo en la página order-tests):
 *   <script>window.ALYX_ORDER_CONFIG = { apiBase, clientId, redirectUri, membershipUrl, orderPageUrl, bearerKey, i18n }</script>
 *   {% if request.page_type == 'page' and page.handle == 'order-tests' %}
 *     <script src="{{ 'alyx-order-tests.js' | asset_url }}" defer></script>
 *   {% endif %}
 *
 * La sección expone:
 *   - window.ALYX_TESTS_HANDLES  : handles de los tests (canjeables con crédito)
 *   - window.ALYX_ALWAYS_EXTRAS  : extras siempre disponibles (bundles, pagados)
 *   - window.ALYX_EXTRAS_MAP     : test handle -> [extras handles permitidos]
 *
 * Autenticación: OAuth 2.0 PKCE contra el Customer Account API, con CALLBACK
 * PROPIO (redirectUri apunta a /pages/order-tests?alyx_order_auth=1).
 * Compatible con alyx-rewards.js (mismas claves de sessionStorage, soporta
 * token en formato JSON y texto plano).
 */
(function () {
  'use strict';

  var CFG = window.ALYX_ORDER_CONFIG || {};
  var API_BASE = CFG.apiBase || 'https://alyx.gedl.cl';
  var STORE_ORIGIN = CFG.storeOrigin || window.location.origin;
  var CLIENT_ID = CFG.clientId || '';
  var REDIRECT_URI = CFG.redirectUri || STORE_ORIGIN + '/pages/order-tests?alyx_order_auth=1';
  var MEMBERSHIP_URL = CFG.membershipUrl || '/products/asset-pack-42828496898-example-product-3';
  var ORDER_PAGE = CFG.orderPageUrl || '/pages/order-tests';
  var BEARER_KEY = CFG.bearerKey || 'alyx_bearer_token';
  var SHARED_BEARER_KEY = 'alyx_api_bearer';
  var CUSTOMER_TOKEN_KEY = CFG.customerTokenKey || 'alyx_customer_access_token';
  var DEBUG = !!CFG.debug || (typeof window.ALYX_DEBUG !== 'undefined' && window.ALYX_DEBUG === true);

  var I18N = Object.assign({
    errorAuth: 'Please log in to continue.',
    errorNoCredits: 'You have no credits available right now.',
    errorTooMany: 'You have {credits} credit(s). Select up to {credits} test(s).',
    errorGeneric: 'Something went wrong. Please try again.',
    errorNetwork: 'We could not reach the service. Please try again in a moment.',
    summary: '{tests} test(s) · {extras} extra(s)'
  }, CFG.i18n || {});

  var root = document.querySelector('[data-alyx-order-tests]');

  if (!root) {
    return;
  }

  var els = {
    creditsWrap: root.querySelector('[data-alyx-credits-wrap]'),
    credits: root.querySelector('[data-alyx-credits]'),
    singleLine: root.querySelector('[data-alyx-single-line]'),
    noCredits: root.querySelector('[data-alyx-no-credits]'),
    singleCreditCta: root.querySelector('[data-alyx-single-credit-cta]'),
    tests: root.querySelectorAll('[data-alyx-test]'),
    extras: root.querySelectorAll('[data-alyx-extra]'),
    testsSection: root.querySelector('[data-alyx-section="tests"]'),
    relatedSection: root.querySelector('[data-alyx-section="related"]'),
    extrasSection: root.querySelector('[data-alyx-section="extras"]'),
    relatedGrid: root.querySelector('[data-alyx-related]'),
    extrasGrid: root.querySelector('[data-alyx-extras]'),
    summaryBar: root.querySelector('.alyx-order-tests__summary'),
    continueBtns: root.querySelectorAll('[data-alyx-continue]'),
    backBtn: root.querySelector('[data-alyx-back]'),
    summary: root.querySelector('[data-alyx-summary]'),
    checkout: root.querySelector('[data-alyx-checkout]'),
    error: root.querySelector('[data-alyx-error]'),
    loading: root.querySelector('[data-alyx-loading]')
  };

  var state = {
    credits: 0,
    single: null,
    busy: false
  };

  var variantIdToHandle = {};

  var productCache = {};

  function log() {
    if (DEBUG) {
      /* eslint-disable no-console */
      console.log.apply(console, ['[alyx-order-tests]'].concat(Array.prototype.slice.call(arguments)));
      /* eslint-enable no-console */
    }
  }

  function showError(message) {
    els.error.textContent = message;
    els.error.hidden = false;
  }

  function hideError() {
    els.error.hidden = true;
  }

  function setLoading(on) {
    state.busy = on;
    els.loading.hidden = !on;
    els.checkout.disabled = on;
    els.backBtn.disabled = on;
  }

  function selectedValues(nodes) {
    var ids = [];
    nodes.forEach(function (node) {
      if (node.checked) {
        ids.push(parseInt(node.value, 10));
      }
    });
    return ids;
  }

  /* ------------------------------------------------------------------ */
  /* Tarjetas de tests/extras (por handle, vía /products/{handle}.js)    */
  /* ------------------------------------------------------------------ */

  function fetchProduct(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { 'Accept': 'application/json' }
    }).then(function (response) {
      if (!response.ok) {
        return null;
      }
      return response.json();
    }).catch(function () {
      return null;
    });
  }

  function ensureProduct(handle) {
    if (productCache[handle]) {
      return Promise.resolve(productCache[handle]);
    }

    return fetchProduct(handle).then(function (product) {
      if (product) {
        productCache[handle] = product;
      }
      return product;
    });
  }

  var priceMap = {};
  var requestedHandles = {};

  function loadPrices(handles) {
    var missing = [];

    handles.forEach(function (h) {
      if (!requestedHandles[h]) {
        requestedHandles[h] = true;
        missing.push(h);
      }
    });

    if (!missing.length) {
      return Promise.resolve();
    }

    var query = missing.map(function (h) {
      return 'handles[]=' + encodeURIComponent(h);
    }).join('&');

    return fetch(API_BASE + '/api/order-tests/prices?' + query, {
      headers: { 'Accept': 'application/json' }
    }).then(function (response) {
      if (!response.ok) {
        return;
      }

      return response.json().then(function (data) {
        var prices = (data && data.prices) || {};

        Object.keys(prices).forEach(function (h) {
          priceMap[h] = prices[h];
        });
      });
    }).catch(function () {
      /* sin precios del backend: se usa el precio del producto */
    });
  }

  function pickVariant(product, wantCredit) {
    var variants = product.variants || [];

    if (!variants.length) {
      return null;
    }

    if (wantCredit) {
      var credit = null;

      for (var i = 0; i < variants.length; i++) {
        if (parseFloat(variants[i].price || 0) === 0) {
          credit = variants[i];
          break;
        }
        if (!credit && (variants[i].title || '').toLowerCase().indexOf('credit') !== -1) {
          credit = variants[i];
        }
      }

      return credit || variants[0];
    }

    for (var j = 0; j < variants.length; j++) {
      if (parseFloat(variants[j].price || 0) > 0) {
        return variants[j];
      }
    }

    return variants[0];
  }

  function buildCard(product, isExtra, creditCost, label) {
    var variant = pickVariant(product, !isExtra);

    if (!variant) {
      return null;
    }

    var span = document.createElement('span');
    span.className = 'alyx-order-tests__card-title';
    span.textContent = product.title || product.handle;

    var small = document.createElement('small');
    small.className = 'alyx-order-tests__card-price';

    var display = null;

    if (isExtra) {
      display = priceMap[product.handle] !== undefined ? priceMap[product.handle] : variant.price;
      small.textContent = '$' + parseFloat(display || 0).toFixed(2);
    } else {
      small.className += ' alyx-order-tests__card-credit-line';
      small.textContent = '1 credit';
    }

    if (!isExtra) {
      var testLabel = document.createElement('label');
      testLabel.className = 'alyx-order-tests__card';

      var input = document.createElement('input');
      input.type = 'radio';
      input.name = 'alyx-test-selection';
      input.setAttribute('data-alyx-test', '');
      input.value = variant.id;

      testLabel.appendChild(input);
      testLabel.appendChild(span);
      testLabel.appendChild(small);

      return testLabel;
    }

    var card = document.createElement('div');
    card.className = 'alyx-order-tests__card';

    var main = document.createElement('label');
    main.className = 'alyx-order-tests__card-main';

    var check = document.createElement('input');
    check.type = 'checkbox';
    check.setAttribute('data-alyx-extra', '');
    check.value = variant.id;

    main.appendChild(check);
    main.appendChild(span);

    if (label) {
      var labelEl = document.createElement('small');
      labelEl.className = 'alyx-order-tests__card-label';
      labelEl.textContent = label;
      main.appendChild(labelEl);
    }

    card.appendChild(main);

    if (creditCost) {
      card.dataset.alyxCost = String(creditCost);
      card.dataset.alyxPrice = String(display || 0);

      var mode = document.createElement('div');
      mode.className = 'alyx-order-tests__card-mode';

      var creditLabel = document.createElement('label');
      var creditRadio = document.createElement('input');
      creditRadio.type = 'radio';
      creditRadio.name = 'alyx-mode-' + variant.id;
      creditRadio.value = 'credit';
      creditRadio.disabled = true;
      creditRadio.setAttribute('data-alyx-mode', 'credit');
      creditLabel.appendChild(creditRadio);
      creditLabel.appendChild(document.createTextNode(' ' + creditCost + ' credit' + (creditCost > 1 ? 's' : '')));
      mode.appendChild(creditLabel);

      var paidLabel = document.createElement('label');
      var paidRadio = document.createElement('input');
      paidRadio.type = 'radio';
      paidRadio.name = 'alyx-mode-' + variant.id;
      paidRadio.value = 'paid';
      paidRadio.checked = true;
      paidRadio.disabled = true;
      paidRadio.setAttribute('data-alyx-mode', 'paid');
      paidLabel.appendChild(paidRadio);
      paidLabel.appendChild(document.createTextNode(' Pay ' + small.textContent));
      mode.appendChild(paidLabel);

      card.appendChild(mode);
    }

    return card;
  }

  function buildTestCards() {
    var testsSection = root.querySelector('[data-alyx-section="tests"]');
    var testsGrid = testsSection ? testsSection.querySelector('[data-alyx-tests]') : null;
    var groups = window.ALYX_TEST_GROUPS || [];

    if (!testsSection || !testsGrid) {
      return Promise.resolve();
    }

    var loadingHint = document.createElement('p');
    loadingHint.className = 'alyx-order-tests__loading-hint';
    loadingHint.textContent = 'Loading tests…';
    testsGrid.innerHTML = '';
    testsGrid.appendChild(loadingHint);

    var flat = [];

    groups.forEach(function (group) {
      (group.items || []).forEach(function (item) {
        (item.handles || []).forEach(function (handle) {
          flat.push({ group: group, handle: handle });
        });
      });
    });

    return Promise.all(flat.map(function (entry) {
      return ensureProduct(entry.handle);
    })).then(function (products) {
      var allHandles = [];

      flat.forEach(function (entry) {
        allHandles.push(entry.handle);
      });

      return loadPrices(allHandles).then(function () {
        var currentGroup = null;
        var currentGrid = null;
        var rendered = 0;

      flat.forEach(function (entry, index) {
        var product = products[index];

        if (!product) {
          return;
        }

        if (entry.group !== currentGroup) {
          currentGroup = entry.group;

          var header = document.createElement('h3');
          header.className = 'alyx-order-tests__group-title';
          header.textContent = currentGroup.title;

          if (currentGroup.subtitle) {
            var small = document.createElement('small');
            small.textContent = currentGroup.subtitle;
            header.appendChild(small);
          }

          testsGrid.appendChild(header);

          currentGrid = document.createElement('div');
          currentGrid.className = 'alyx-order-tests__grid';
          testsGrid.appendChild(currentGrid);
        }

        var variant = pickVariant(product, true);

        if (variant) {
          variantIdToHandle[variant.id] = entry.handle;
        }

        var card = buildCard(product, false);

        if (card && currentGrid) {
          currentGrid.appendChild(card);
          rendered++;
        }
      });

      if (rendered > 0) {
        var hint = testsGrid.querySelector('.alyx-order-tests__loading-hint');
        if (hint) {
          hint.remove();
        }
      }

      });
    });
  }

  function renderGrid(grid, handles, creditCost, labels) {
    if (!grid) {
      return Promise.resolve();
    }

    grid.innerHTML = '';

    return Promise.all(handles.map(function (handle) {
      return ensureProduct(handle);
    })).then(function (products) {
      return loadPrices(handles).then(function () {
        products.forEach(function (product) {
          if (!product) {
            return;
          }

          var card = buildCard(product, true, creditCost, labels ? labels[product.handle] : null);

          if (card) {
            grid.appendChild(card);
          }
        });
      });
    });
  }

  function renderRelated() {
    var map = window.ALYX_EXTRAS_MAP || {};
    var labelMap = window.ALYX_RELATED_LABELS || {};
    var selected = selectedValues(els.tests);

    if (selected.length !== 1) {
      return Promise.resolve();
    }

    var handle = variantIdToHandle[selected[0]];
    var related = (handle && map[handle]) || [];

    return renderGrid(els.relatedGrid, related, 1, (handle && labelMap[handle]) || {});
  }

  function renderBundles() {
    var always = window.ALYX_ALWAYS_EXTRAS || [];

    return renderGrid(els.extrasGrid, always, 3);
  }

  function refreshExtraListeners() {
    els.extras = root.querySelectorAll('[data-alyx-extra]');
    els.modes = root.querySelectorAll('[data-alyx-mode]');

    els.extras.forEach(function (node) {
      node.addEventListener('change', onSelectionChange);
    });

    els.modes.forEach(function (node) {
      node.addEventListener('change', onSelectionChange);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Sesión: bearer token de la API Alyx                                 */
  /* ------------------------------------------------------------------ */

  function base64Url(bytes) {
    var binary = '';
    var chunk = 0x8000;
    for (var i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function sha256(plain) {
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(plain));
  }

  function randomBytes(length) {
    var bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    return bytes;
  }

  function parseToken(raw) {
    if (!raw) {
      return null;
    }
    try {
      var parsed = JSON.parse(raw);
      if (parsed && typeof parsed.token === 'string') {
        return parsed.token;
      }
    } catch (e) {
      /* no es JSON */
    }
    return raw;
  }

  function getBearer() {
    var own = sessionStorage.getItem(BEARER_KEY);

    if (own && own !== 'undefined' && own !== 'null') {
      return own;
    }

    var shared = parseToken(sessionStorage.getItem(SHARED_BEARER_KEY));

    if (shared && shared !== 'undefined' && shared !== 'null') {
      return shared;
    }

    return null;
  }

  function setBearer(token) {
    sessionStorage.setItem(BEARER_KEY, token);
    sessionStorage.setItem(SHARED_BEARER_KEY, JSON.stringify({
      token: token,
      expires: Date.now() + 5 * 3600 * 1000
    }));
  }

  function getCustomerToken() {
    return parseToken(sessionStorage.getItem(CUSTOMER_TOKEN_KEY));
  }

  function setCustomerToken(token) {
    sessionStorage.setItem(CUSTOMER_TOKEN_KEY, JSON.stringify({
      token: token,
      expires: Date.now() + 24 * 3600 * 1000
    }));
  }

  function resetSession() {
    sessionStorage.removeItem(BEARER_KEY);
    sessionStorage.removeItem(SHARED_BEARER_KEY);
    sessionStorage.removeItem(CUSTOMER_TOKEN_KEY);
    sessionStorage.removeItem('alyx_order_verifier');
    sessionStorage.removeItem('alyx_order_state');
    sessionStorage.removeItem('alyx_order_attempted');
    sessionStorage.removeItem('alyx_rewards_attempted');
  }

  function exchangeCustomerToken(customerToken) {
    return fetch(API_BASE + '/api/auth/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ token: customerToken })
    }).then(function (response) {
      if (response.status === 401) {
        resetSession();
        throw new Error('auth');
      }
      if (response.status >= 500) {
        throw new Error('network');
      }
      if (!response.ok) {
        throw new Error('auth');
      }
      return response.json();
    }).then(function (data) {
      if (!data.token) {
        throw new Error('network');
      }
      setBearer(data.token);
      sessionStorage.removeItem('alyx_order_attempted');
      return data.token;
    });
  }

  function discover() {
    var cached = sessionStorage.getItem('alyx_order_discovery');

    if (cached) {
      try {
        return Promise.resolve(JSON.parse(cached));
      } catch (e) {
        /* cache corrupto, se ignora */
      }
    }

    return fetch(STORE_ORIGIN + '/.well-known/openid-configuration')
      .then(function (response) {
        if (!response.ok) {
          throw new Error('network');
        }
        return response.json();
      })
      .then(function (discovery) {
        if (!discovery.authorization_endpoint || !discovery.token_endpoint) {
          throw new Error('network');
        }
        sessionStorage.setItem('alyx_order_discovery', JSON.stringify(discovery));

        return discovery;
      });
  }

  function silentLogin() {
    // Frenado anti-loop: maximo 1 navegacion al authorize por minuto.
    var lastLogin = parseInt(sessionStorage.getItem('alyx_order_login_at') || '0', 10);
    var now = Date.now();

    if (now - lastLogin < 60000) {
      return Promise.reject(new Error('auth'));
    }

    sessionStorage.setItem('alyx_order_login_at', String(now));

    return discover().then(function (discovery) {
      var verifier = base64Url(randomBytes(32));
      var stateValue = base64Url(randomBytes(16));

      return sha256(verifier).then(function (digest) {
        sessionStorage.setItem('alyx_order_verifier', verifier);
        sessionStorage.setItem('alyx_order_state', stateValue);

        var params = new URLSearchParams({
          client_id: CLIENT_ID,
          redirect_uri: REDIRECT_URI,
          response_type: 'code',
          scope: 'openid email customer-account-api:full',
          state: stateValue,
          code_challenge: base64Url(new Uint8Array(digest)),
          code_challenge_method: 'S256',
          prompt: 'none'
        });

        window.location.href = discovery.authorization_endpoint + '?' + params.toString();
      });
    });
  }

  /**
   * Procesa el callback OAuth cuando la página se recarga con ?alyx_order_auth=1
   * (el redirect_uri apunta a la propia página de tests).
   */
  function handleRedirectCallback() {
    var params = new URLSearchParams(window.location.search);

    if (!params.has('code') && !params.has('error')) {
      return Promise.resolve(null);
    }

    var stateValue = params.get('state');
    var verifier = sessionStorage.getItem('alyx_order_verifier');
    var storedState = sessionStorage.getItem('alyx_order_state');

    sessionStorage.removeItem('alyx_order_verifier');
    sessionStorage.removeItem('alyx_order_state');

    if (!stateValue || !storedState || stateValue !== storedState || params.get('error')) {
      resetSession();
      history.replaceState(null, '', window.location.pathname + window.location.hash);

      return Promise.resolve(null);
    }

    return discover().then(function (discovery) {
      var body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: CLIENT_ID,
        code: params.get('code'),
        redirect_uri: REDIRECT_URI,
        code_verifier: verifier
      });

      return fetch(discovery.token_endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });
    }).then(function (response) {
      if (response.status >= 500) {
        throw new Error('network');
      }
      if (!response.ok) {
        throw new Error('auth');
      }
      return response.json();
    }).then(function (data) {
      if (!data.access_token) {
        throw new Error('auth');
      }
      setCustomerToken(data.access_token);
      history.replaceState(null, '', window.location.pathname + window.location.hash);

      return exchangeCustomerToken(data.access_token);
    }).catch(function (error) {
      resetSession();
      history.replaceState(null, '', window.location.pathname + window.location.hash);
      throw error;
    });
  }

  function ensureBearer() {
    var existing = getBearer();

    if (existing) {
      return Promise.resolve(existing);
    }

    // Si la URL trae callback OAuth, procesarlo PRIMERO: nunca descartar el code
    // ni disparar otro silent login encima del callback (causa de loops).
    var params = new URLSearchParams(window.location.search);

    if (params.get('alyx_order_auth') !== null && (params.has('code') || params.has('error'))) {
      return handleRedirectCallback().then(function (token) {
        if (token) {
          return token;
        }
        throw new Error('auth');
      });
    }

    var customerToken = getCustomerToken();

    if (customerToken) {
      return exchangeCustomerToken(customerToken).catch(function (error) {
        if (error.message === 'auth') {
          resetSession();

          var attempts = parseInt(sessionStorage.getItem('alyx_order_auth_attempts') || '0', 10);
          var lastAttempt = parseInt(sessionStorage.getItem('alyx_order_auth_last') || '0', 10);
          var now = Date.now();

          if (attempts >= 3 && (now - lastAttempt) < 300000) {
            sessionStorage.setItem('alyx_order_auth_attempts', '0');

            return Promise.reject(new Error('auth'));
          }

          sessionStorage.setItem('alyx_order_auth_attempts', String(attempts + 1));
          sessionStorage.setItem('alyx_order_auth_last', String(now));

          return silentLogin();
        }

        throw error;
      });
    }

    if (!sessionStorage.getItem('alyx_order_attempted')) {
      sessionStorage.setItem('alyx_order_attempted', '1');

      return silentLogin();
    }

    return handleRedirectCallback().then(function (token) {
      if (token) {
        return token;
      }
      throw new Error('auth');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Créditos y selección                                                */
  /* ------------------------------------------------------------------ */

  function loadCredits() {
    return ensureBearer().then(function (bearer) {
      if (!bearer) {
        throw new Error('auth');
      }

      return fetch(API_BASE + '/api/me/reward', {
        headers: { 'Authorization': 'Bearer ' + bearer, 'Accept': 'application/json' }
      });
    }).then(function (response) {
      if (response.status === 401) {
        resetSession();
        throw new Error('auth');
      }
      if (!response.ok) {
        throw new Error('network');
      }
      return response.json();
    }).then(function (data) {
      var reward = data.reward;

      if (!reward) {
        state.credits = 0;
        state.single = null;
        return;
      }

      state.single = reward.single_credits && reward.single_credits.available
        ? reward.single_credits
        : null;
      state.credits = (reward.pending || 0) + ((state.single && state.single.available) || 0);
    });
  }

  function renderCredits() {
    if (!els.credits) {
      return;
    }

    els.credits.textContent = Math.max(0, state.credits - creditUsedCount());

    if (els.singleLine && state.single && state.single.available) {
      var expiry = state.single.expires_at ? new Date(state.single.expires_at) : null;
      var suffix = expiry ? ' (valid until ' + expiry.toISOString().slice(0, 10) + ')' : '';

      els.singleLine.textContent = 'Single Test Credits: ' + state.single.available + suffix;
      els.singleLine.hidden = false;
    } else if (els.singleLine) {
      els.singleLine.hidden = true;
    }
  }

  function creditUsedCount() {
    var used = selectedValues(els.tests).length;

    root.querySelectorAll('.alyx-order-tests__card[data-alyx-cost]').forEach(function (card) {
      var check = card.querySelector('[data-alyx-extra]');
      var credit = card.querySelector('[data-alyx-mode="credit"]');

      if (check && check.checked && credit && credit.checked) {
        used += parseInt(card.dataset.alyxCost, 10) || 0;
      }
    });

    return used;
  }

  function paidExtrasValues() {
    var values = [];

    root.querySelectorAll('.alyx-order-tests__card[data-alyx-cost]').forEach(function (card) {
      var check = card.querySelector('[data-alyx-extra]');
      var paid = card.querySelector('[data-alyx-mode="paid"]');

      if (check && check.checked && paid && paid.checked) {
        values.push(parseInt(check.value, 10));
      }
    });

    return values;
  }

  function creditExtrasValues() {
    var values = [];

    root.querySelectorAll('.alyx-order-tests__card[data-alyx-cost]').forEach(function (card) {
      var check = card.querySelector('[data-alyx-extra]');
      var credit = card.querySelector('[data-alyx-mode="credit"]');

      if (check && check.checked && credit && credit.checked) {
        values.push(parseInt(check.value, 10));
      }
    });

    return values;
  }

  function paidTotal() {
    var total = 0;

    root.querySelectorAll('.alyx-order-tests__card[data-alyx-cost]').forEach(function (card) {
      var check = card.querySelector('[data-alyx-extra]');
      var paid = card.querySelector('[data-alyx-mode="paid"]');

      if (check && check.checked && paid && paid.checked) {
        total += parseFloat(card.dataset.alyxPrice || '0') || 0;
      }
    });

    return total;
  }

  function updateModeAvailability() {
    var used = creditUsedCount();

    root.querySelectorAll('.alyx-order-tests__card[data-alyx-cost]').forEach(function (card) {
      var check = card.querySelector('[data-alyx-extra]');
      var credit = card.querySelector('[data-alyx-mode="credit"]');
      var paid = card.querySelector('[data-alyx-mode="paid"]');

      if (!check || !credit || !paid) {
        return;
      }

      if (!check.checked) {
        credit.disabled = true;
        paid.disabled = true;
        return;
      }

      var cost = parseInt(card.dataset.alyxCost, 10) || 0;
      var inCredit = credit.checked;
      var needed = used - (inCredit ? cost : 0) + cost;

      credit.disabled = needed > state.credits;
      paid.disabled = false;
    });
  }

  function updateSummary() {
    var credits = creditUsedCount();
    var paid = paidTotal();

    renderCredits();

    if (credits === 0) {
      els.summary.textContent = 'No tests selected.';
    } else {
      els.summary.textContent = credits + ' credit(s) · $' + paid.toFixed(2) + ' to pay';
    }

    els.checkout.disabled = credits === 0 || state.busy;
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goToStep2() {
    if (state.busy) {
      return;
    }

    var selected = selectedValues(els.tests);

    if (selected.length !== 1) {
      showError('Please select a test to continue.');
      return;
    }

    state.busy = true;

    Promise.all([renderRelated(), renderBundles()]).then(function () {
      els.testsSection.hidden = true;
      els.relatedSection.hidden = els.relatedGrid.children.length === 0;
      els.extrasSection.hidden = els.extrasGrid.children.length === 0;
      els.summaryBar.hidden = false;

      refreshExtraListeners();
      updateModeAvailability();

      state.busy = false;
      updateSummary();
      hideError();
      scrollToTop();
    }).catch(function () {
      state.busy = false;
      updateSummary();
    });
  }

  function goToStep1() {
    if (state.busy) {
      return;
    }

    els.testsSection.hidden = false;
    els.relatedSection.hidden = true;
    els.extrasSection.hidden = true;
    els.summaryBar.hidden = true;
    updateSummary();
    scrollToTop();
  }

  function onSelectionChange(event) {
    hideError();

    var isTestChange = event.target.dataset.alyxTest !== undefined;

    if (state.credits === 0 && isTestChange) {
      event.target.checked = false;
      showError(I18N.errorNoCredits);
      return;
    }

    updateModeAvailability();
    updateSummary();
  }

  /* ------------------------------------------------------------------ */
  /* Preparar pedido y pasar al checkout                                 */
  /* ------------------------------------------------------------------ */

  function clearCart() {
    return fetch('/cart/clear.js', { method: 'POST' }).then(function (response) {
      if (!response.ok) {
        throw new Error('network');
      }
      return response.json();
    });
  }

  function captureCart() {
    return fetch('/cart.js').then(function (response) {
      if (!response.ok) {
        throw new Error('network');
      }
      return response.json();
    }).then(function (cart) {
      return (cart.items || []).map(function (item) {
        return { id: item.variant_id, quantity: item.quantity, properties: item.properties || {} };
      });
    }).catch(function () {
      return [];
    });
  }

  function addToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('network');
      }
      return response.json();
    });
  }

  function goToCheckout(code) {
    window.location.href = '/checkout?discount=' + encodeURIComponent(code);
  }

  function onCheckout() {
    if (state.busy) {
      return;
    }

    hideError();

    var tests = selectedValues(els.tests);
    var extras = paidExtrasValues();
    var credits = creditExtrasValues();

    if (tests.length === 0) {
      showError(I18N.errorGeneric);
      return;
    }

    if (creditUsedCount() > state.credits) {
      showError(I18N.errorTooMany.replace(/\{credits\}/g, state.credits));
      return;
    }

    setLoading(true);

    ensureBearer()
      .then(function (bearer) {
        return fetch(API_BASE + '/api/me/order/prepare', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': 'Bearer ' + bearer
          },
          body: JSON.stringify({ tests: tests, extras: extras, credits: credits })
        });
      })
      .then(function (response) {
        if (response.status === 401) {
          resetSession();
          throw new Error('auth');
        }
        if (response.status >= 500) {
          throw new Error('network');
        }
        return response.json().then(function (data) {
          if (!response.ok) {
            var error = new Error(data && data.error ? data.error : 'network');
            error.status = response.status;
            error.body = data;
            throw error;
          }
          return data;
        });
      })
      .then(function (data) {
        var items = tests.map(function (id) {
          return { id: id, quantity: 1, properties: { _alyx_credit: '1' } };
        }).concat(credits.map(function (id) {
          var cost = '1';

          root.querySelectorAll('.alyx-order-tests__card[data-alyx-cost]').forEach(function (card) {
            var check = card.querySelector('[data-alyx-extra]');
            var credit = card.querySelector('[data-alyx-mode="credit"]');

            if (check && credit && credit.checked && parseInt(check.value, 10) === id) {
              cost = card.dataset.alyxCost;
            }
          });

          return { id: id, quantity: 1, properties: { _alyx_credit: cost } };
        })).concat(extras.map(function (id) {
          return { id: id, quantity: 1 };
        }));

        return captureCart().then(function (previousItems) {
          return clearCart().then(function () {
            return addToCart(items);
          }).catch(function (error) {
            if (previousItems.length > 0) {
              return addToCart(previousItems).then(function () {
                throw error;
              });
            }
            throw error;
          });
        }).then(function () {
          goToCheckout(data.code);
        });
      })
      .catch(function (error) {
        log('error', error);

        setLoading(false);

        if (error && error.status === 403) {
          window.location.href = MEMBERSHIP_URL;
          return;
        }
        if (error && (error.status === 409 || error.status === 422)) {
          showError(error.body && error.body.error ? error.body.error : I18N.errorGeneric);
          return;
        }
        if (error && error.message === 'auth') {
          showError(I18N.errorAuth);
          return;
        }
        if (error && error.message === 'network') {
          showError(I18N.errorNetwork);
          return;
        }
        showError(I18N.errorGeneric);
      });
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */

  function init() {
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        setLoading(false);
        hideError();
        updateSummary();
      }
    });

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && state.busy) {
        setLoading(false);
        hideError();
        updateSummary();
      }
    });

    buildTestCards()
      .then(function () {
        els.tests = root.querySelectorAll('[data-alyx-test]');

        els.tests.forEach(function (node) {
          node.addEventListener('change', onSelectionChange);
        });

        els.continueBtns.forEach(function (btn) {
          btn.addEventListener('click', goToStep2);
        });
        els.backBtn.addEventListener('click', goToStep1);
        els.checkout.addEventListener('click', onCheckout);

        return loadCredits();
      })
      .then(function () {
        els.creditsWrap.hidden = false;
        renderCredits();

        if (els.singleCreditCta) {
          els.singleCreditCta.href = (window.ALYX_SINGLE_CREDIT_URL || '/products/alyx-single-test-credit');
        }

        if (state.credits === 0) {
          els.tests.forEach(function (node) {
            node.disabled = true;
          });

          if (els.noCredits) {
            els.noCredits.hidden = false;
          }

          showError(I18N.errorNoCredits);
          els.checkout.disabled = true;
        } else {
          updateSummary();
        }
      })
      .catch(function (error) {
        log('error loading credits', error);

        if (error && error.message === 'auth') {
          sessionStorage.removeItem('alyx_order_attempted');
          showError(I18N.errorAuth);
        } else {
          showError(I18N.errorNetwork);
        }
        els.checkout.disabled = true;
      });
  }

  init();
})();